import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Plus, Lock, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { type RoleDto, type NavItem, type Action, type ApiError } from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchWithAuth } from "@/lib/auth-client";

// US-054 (ADR-017) — Admin manages custom roles. List of all roles with
// nav/action/user counts; System Admin is read-only (lock icon, no
// edit/delete). Add/Edit open a form with a permission matrix (rows =
// resources, columns = Create/View/Update/Delete/Submit) that toggles
// membership in the role's nav_items + actions sets; Delete is confirmed
// and refused while users are assigned (the API enforces both regardless).

// ── Permission matrix definition ───────────────────────────────────────────
// The grid is a presentation layer over the role's nav_items: NavItem[] and
// actions: Action[] sets. Each row is a resource; each column maps to either a
// NavItem (View) or a single Action (Create/Update/Delete/Submit), or is
// not-applicable ("—"). Every NavItem and Action appears in AT MOST one cell.
//
// A View cell maps to one or more nav items (the field is an array); it shows
// checked when ANY is present and toggling sets/clears all. Nav items not used
// by any cell (e.g. the requester-personal `my_prs`) are preserved on save via
// GRID_NAV_KEYS rather than edited here.

type MatrixColumn = "view" | "create" | "update" | "delete" | "submit";

const MATRIX_COLUMNS: { key: MatrixColumn; label: string }[] = [
  { key: "view", label: "View" },
  { key: "create", label: "Create" },
  { key: "update", label: "Update" },
  { key: "delete", label: "Delete" },
  { key: "submit", label: "Submit" },
];

interface MatrixRow {
  label: string;
  /** NavItem(s) the View cell toggles; first one supplies the testid suffix. */
  view: NavItem[] | null;
  create: Action | null;
  update: Action | null;
  delete: Action | null;
  submit: Action | null;
}

const MATRIX_ROWS: MatrixRow[] = [
  {
    label: "Dashboard",
    view: ["dashboard"],
    create: null,
    update: null,
    delete: null,
    submit: null,
  },
  // View grants the Purchase Requests list (`prs`). The requester-personal
  // `my_prs` screen is not a grid cell — it's preserved untouched on save.
  {
    label: "Purchase Requests",
    view: ["prs"],
    create: "create_pr",
    update: null,
    delete: null,
    submit: "submit_pr",
  },
  {
    label: "Approvals",
    view: ["approval_queue"],
    create: null,
    update: null,
    delete: null,
    submit: null,
  },
  {
    label: "RFQs",
    view: ["rfqs"],
    create: "create_rfq",
    update: null,
    delete: null,
    submit: "distribute_rfq",
  },
  {
    label: "Purchase Orders",
    view: ["purchase_orders"],
    create: "generate_po",
    update: null,
    delete: null,
    submit: null,
  },
  {
    label: "Goods Receipts",
    view: ["grns"],
    create: "receive_goods",
    update: null,
    delete: null,
    submit: null,
  },
  {
    label: "Invoices",
    view: ["invoices"],
    create: "create_invoice",
    update: null,
    delete: null,
    submit: null,
  },
  {
    label: "Vendors",
    view: ["vendors"],
    create: "create_vendor",
    update: null,
    delete: null,
    submit: null,
  },
  {
    label: "Users",
    view: ["user_management"],
    create: "invite_user",
    update: "manage_user",
    delete: null,
    submit: null,
  },
  {
    label: "Item Master",
    view: ["item_master"],
    create: "manage_item_master",
    update: null,
    delete: null,
    submit: null,
  },
  { label: "Reports", view: ["reports"], create: null, update: null, delete: null, submit: null },
  {
    label: "Notifications",
    view: ["notifications"],
    create: null,
    update: null,
    delete: null,
    submit: null,
  },
  {
    label: "Match Policy",
    view: ["match_policy"],
    create: null,
    update: null,
    delete: null,
    submit: null,
  },
  { label: "Settings", view: ["settings"], create: null, update: null, delete: null, submit: null },
];

// Action keys the grid surfaces (the editable CRUD/Submit set). Any action a
// role holds that is NOT in this set is a workflow action (approve/reject/
// award/cancel/hold/override/pay/etc.) the grid cannot edit — it is preserved
// untouched on save so editing a role never silently drops those grants.
const GRID_ACTION_KEYS: ReadonlySet<Action> = new Set(
  MATRIX_ROWS.flatMap((r) =>
    [r.create, r.update, r.delete, r.submit].filter((a): a is Action => a !== null),
  ),
);

// NavItems the grid's View cells surface. Any nav a role holds that is NOT here
// (e.g. the requester-personal `my_prs`) is preserved untouched on save so
// editing a role through the grid never silently drops it.
const GRID_NAV_KEYS: ReadonlySet<NavItem> = new Set(MATRIX_ROWS.flatMap((r) => r.view ?? []));

export const Route = createFileRoute("/settings/roles")({
  head: () => ({
    meta: [
      { title: "Roles — ProcureFlow" },
      { name: "description", content: "Create and manage roles and their permissions." },
    ],
  }),
  component: RolesPage,
});

type FetchState =
  | { kind: "loading" }
  | { kind: "loaded"; roles: RoleDto[] }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

interface FormState {
  open: boolean;
  /** null = creating; otherwise editing this role. */
  editing: RoleDto | null;
  name: string;
  navItems: Set<NavItem>;
  actions: Set<Action>;
  nameError: string | null;
  saving: boolean;
}

const EMPTY_FORM: FormState = {
  open: false,
  editing: null,
  name: "",
  navItems: new Set(),
  actions: new Set(),
  nameError: null,
  saving: false,
};

function RolesPage() {
  const [state, setState] = useState<FetchState>({ kind: "loading" });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleting, setDeleting] = useState<RoleDto | null>(null);

  async function load() {
    if (import.meta.env.DEV) {
      const { mswReady } = await import("@/mocks/browser");
      await mswReady;
    }
    try {
      const res = await fetchWithAuth("/api/roles");
      if (res.status === 403) {
        setState({ kind: "forbidden" });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        setState({ kind: "error", message: body.message ?? "Failed to load roles." });
        return;
      }
      const { data } = (await res.json()) as { data: RoleDto[] };
      setState({ kind: "loaded", roles: data });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Unexpected error.",
      });
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function openCreate() {
    setForm({ ...EMPTY_FORM, open: true, navItems: new Set(), actions: new Set() });
  }

  function openEdit(role: RoleDto) {
    setForm({
      open: true,
      editing: role,
      name: role.name,
      navItems: new Set(role.nav_items),
      actions: new Set(role.actions),
      nameError: null,
      saving: false,
    });
  }

  function toggleNav(items: NavItem[], checked: boolean) {
    setForm((f) => {
      const next = new Set(f.navItems);
      for (const item of items) {
        if (checked) next.add(item);
        else next.delete(item);
      }
      return { ...f, navItems: next };
    });
  }

  function toggleAction(item: Action, checked: boolean) {
    setForm((f) => {
      const next = new Set(f.actions);
      if (checked) next.add(item);
      else next.delete(item);
      return { ...f, actions: next };
    });
  }

  // Set every available (non-"—") cell in scope on or off. `scope` filters the
  // matrix to a single column; omit it for the master select-all.
  function setAllCells(checked: boolean, scope?: MatrixColumn) {
    setForm((f) => {
      const nav = new Set(f.navItems);
      const acts = new Set(f.actions);
      for (const row of MATRIX_ROWS) {
        if ((!scope || scope === "view") && row.view) {
          for (const n of row.view) {
            if (checked) nav.add(n);
            else nav.delete(n);
          }
        }
        for (const col of ["create", "update", "delete", "submit"] as const) {
          if (scope && scope !== col) continue;
          const action = row[col];
          if (action) {
            if (checked) acts.add(action);
            else acts.delete(action);
          }
        }
      }
      return { ...f, navItems: nav, actions: acts };
    });
  }

  // Toggle every available cell in a single row.
  function setRowCells(row: MatrixRow, checked: boolean) {
    if (row.view) toggleNav(row.view, checked);
    for (const col of ["create", "update", "delete", "submit"] as const) {
      const action = row[col];
      if (action) toggleAction(action, checked);
    }
  }

  // Is a View cell on? Checked when ANY of its nav items is present.
  function isViewChecked(items: NavItem[]): boolean {
    return items.some((n) => form.navItems.has(n));
  }

  // Tri-state for a select-all checkbox: true = all on, "indeterminate" = some
  // on, false = none on. `on`/`total` are counts of available cells in scope.
  function triState(on: number, total: number): boolean | "indeterminate" {
    if (total === 0 || on === 0) return false;
    return on === total ? true : "indeterminate";
  }

  // Collect every available cell's on/total counts, optionally scoped.
  function scopeCounts(rows: MatrixRow[], scope?: MatrixColumn): [number, number] {
    let on = 0;
    let total = 0;
    for (const row of rows) {
      if ((!scope || scope === "view") && row.view) {
        total += 1;
        if (isViewChecked(row.view)) on += 1;
      }
      for (const col of ["create", "update", "delete", "submit"] as const) {
        if (scope && scope !== col) continue;
        const action = row[col];
        if (action) {
          total += 1;
          if (form.actions.has(action)) on += 1;
        }
      }
    }
    return [on, total];
  }

  const masterState = (() => {
    const [on, total] = scopeCounts(MATRIX_ROWS);
    return triState(on, total);
  })();

  function columnState(col: MatrixColumn): boolean | "indeterminate" {
    const [on, total] = scopeCounts(MATRIX_ROWS, col);
    return triState(on, total);
  }

  function rowState(row: MatrixRow): boolean | "indeterminate" {
    const [on, total] = scopeCounts([row]);
    return triState(on, total);
  }

  // Render one matrix cell: a Checkbox if the resource supports the column,
  // otherwise a muted "—". View cells map to NavItem(s); the others map to a
  // single Action. Testids preserve the legacy convention so the e2e spec
  // (nav-${navItem}, action-${actionId}) keeps passing unmodified.
  function renderCell(row: MatrixRow, col: MatrixColumn) {
    if (col === "view") {
      if (!row.view) return <span className="text-muted-foreground">—</span>;
      const items = row.view;
      return (
        <Checkbox
          checked={isViewChecked(items)}
          onCheckedChange={(c) => toggleNav(items, c === true)}
          aria-label={`View ${row.label}`}
          data-testid={`nav-${items[0]}`}
        />
      );
    }
    const action = row[col];
    if (!action) return <span className="text-muted-foreground">—</span>;
    return (
      <Checkbox
        checked={form.actions.has(action)}
        onCheckedChange={(c) => toggleAction(action, c === true)}
        aria-label={`${col} ${row.label}`}
        data-testid={`action-${action}`}
      />
    );
  }

  async function submitForm() {
    const name = form.name.trim();
    if (name.length === 0) {
      setForm((f) => ({ ...f, nameError: "Role name is required." }));
      return;
    }
    if (name.length > 64) {
      setForm((f) => ({ ...f, nameError: "Role name must be 64 characters or fewer." }));
      return;
    }
    setForm((f) => ({ ...f, saving: true, nameError: null }));

    const editing = form.editing;

    // Preserve workflow actions the CRUD grid does NOT surface (approve_pr,
    // reject_pr, award_quote, cancel_po, hold/unhold/approve/reject/override/
    // mark_invoice_paid, approve_vendor, request_changes_pr, …). The grid only
    // toggles GRID_ACTION_KEYS; any other action the role already holds is kept
    // so editing a role never silently strips its workflow grants. A brand-new
    // role has none to preserve.
    const preservedActions = (editing?.actions ?? []).filter((a) => !GRID_ACTION_KEYS.has(a));
    const actions = Array.from(new Set([...form.actions, ...preservedActions]));

    // Preserve nav items the grid doesn't surface (e.g. my_prs) the same way.
    const preservedNavs = (editing?.nav_items ?? []).filter((n) => !GRID_NAV_KEYS.has(n));
    const nav_items = Array.from(new Set([...form.navItems, ...preservedNavs]));

    const payload = { name, nav_items, actions };
    const res = await fetchWithAuth(editing ? `/api/roles/${editing.id}` : "/api/roles", {
      method: editing ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      toast.success(editing ? "Role updated." : "Role created.");
      setForm(EMPTY_FORM);
      void load();
      return;
    }
    const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
    if (body.code === "DUPLICATE_ROLE_NAME") {
      setForm((f) => ({ ...f, saving: false, nameError: "A role with this name already exists." }));
      return;
    }
    setForm((f) => ({ ...f, saving: false }));
    toast.error(body.message ?? "Failed to save role.");
  }

  async function confirmDelete() {
    if (!deleting) return;
    const res = await fetchWithAuth(`/api/roles/${deleting.id}`, { method: "DELETE" });
    setDeleting(null);
    if (res.status === 204) {
      toast.success("Role deleted.");
      void load();
      return;
    }
    const body = (await res.json().catch(() => ({}))) as Partial<ApiError>;
    toast.error(body.message ?? "Failed to delete role.");
  }

  if (state.kind === "loading") {
    return (
      <div className="space-y-6" data-testid="roles-section">
        <PageHeader title="Roles" description="Loading…" />
      </div>
    );
  }
  if (state.kind === "forbidden") {
    return (
      <div className="space-y-6" data-testid="roles-section">
        <PageHeader title="Roles" description="You don't have access to role management." />
        <div
          className="rounded-2xl bg-card border border-border p-10 text-sm text-muted-foreground shadow-elegant"
          data-testid="roles-forbidden"
        >
          This page is restricted to administrators.
        </div>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="space-y-6" data-testid="roles-section">
        <PageHeader title="Roles" description="Something went wrong." />
        <div className="rounded-2xl bg-card border border-border p-10 text-sm text-destructive shadow-elegant">
          {state.message}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-section">
      <PageHeader
        title="Roles"
        description="Create and manage roles and their permissions."
        action={
          <Button onClick={openCreate} data-testid="add-role-button">
            <Plus className="mr-2 h-4 w-4" />
            Add role
          </Button>
        }
      />

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Nav items</TableHead>
              <TableHead className="text-right">Actions</TableHead>
              <TableHead className="text-right">Users</TableHead>
              <TableHead className="text-right">Manage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {state.roles.map((role) => (
              <TableRow key={role.id} data-testid={`role-row-${role.id}`}>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-2">
                    {role.is_system_admin && (
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Read-only" />
                    )}
                    {role.name}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{role.nav_items.length}</TableCell>
                <TableCell className="text-right tabular-nums">{role.actions.length}</TableCell>
                <TableCell className="text-right tabular-nums">{role.user_count}</TableCell>
                <TableCell className="text-right">
                  {role.is_system_admin ? (
                    <span className="text-xs text-muted-foreground">Read-only</span>
                  ) : (
                    <div className="inline-flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(role)}
                        data-testid={`edit-role-${role.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={role.user_count > 0}
                        title={
                          role.user_count > 0
                            ? `Assigned to ${role.user_count} user${role.user_count === 1 ? "" : "s"}`
                            : "Delete role"
                        }
                        onClick={() => setDeleting(role)}
                        data-testid={`delete-role-${role.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit form */}
      <Dialog open={form.open} onOpenChange={(open) => !open && setForm(EMPTY_FORM)}>
        <DialogContent dismissable={false} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.editing ? "Edit role" : "Add role"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="role-name">Role name</Label>
              <Input
                id="role-name"
                value={form.name}
                maxLength={64}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, nameError: null }))}
                data-testid="role-name-input"
              />
              {form.nameError && (
                <p className="text-sm text-destructive" data-testid="role-name-error">
                  {form.nameError}
                </p>
              )}
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Permissions</legend>
              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[1%] whitespace-nowrap">
                        {/* Master select-all: checks/clears EVERY available cell. */}
                        <Checkbox
                          checked={masterState}
                          onCheckedChange={(c) => setAllCells(c === true)}
                          aria-label="Select all permissions"
                          data-testid="select-all-permissions"
                        />
                      </TableHead>
                      <TableHead>Form</TableHead>
                      {MATRIX_COLUMNS.map((col) => (
                        <TableHead key={col.key} className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span>{col.label}</span>
                            <Checkbox
                              checked={columnState(col.key)}
                              onCheckedChange={(c) => setAllCells(c === true, col.key)}
                              aria-label={`Select all ${col.label}`}
                              data-testid={`select-all-${col.key}`}
                            />
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {MATRIX_ROWS.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>
                          {/* Per-row select-all: toggles every available cell in this row. */}
                          <Checkbox
                            checked={rowState(row)}
                            onCheckedChange={(c) => setRowCells(row, c === true)}
                            aria-label={`Select all ${row.label}`}
                            data-testid={`select-all-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium whitespace-nowrap">{row.label}</TableCell>
                        {MATRIX_COLUMNS.map((col) => {
                          const cell = renderCell(row, col.key);
                          return (
                            <TableCell key={col.key} className="text-center">
                              {cell}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </fieldset>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(EMPTY_FORM)} disabled={form.saving}>
              Cancel
            </Button>
            <Button onClick={submitForm} disabled={form.saving} data-testid="save-role-button">
              {form.saving ? "Saving…" : form.editing ? "Save changes" : "Create role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the role. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-role">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
